import React from 'react';
import { DollarSign, CreditCard } from 'lucide-react';

interface PaymentOptionsFormProps {
  paymentType: 'free' | 'subscription';
  subscriptionPriceCents: number | null;
  subscriptionLimit: number | null;
  onPaymentTypeChange: (type: 'free' | 'subscription') => void;
  onSubscriptionPriceChange: (price: number | null) => void;
  onSubscriptionLimitChange: (limit: number | null) => void;
}

export default function PaymentOptionsForm({
  paymentType,
  subscriptionPriceCents,
  subscriptionLimit,
  onPaymentTypeChange,
  onSubscriptionPriceChange,
  onSubscriptionLimitChange
}: PaymentOptionsFormProps) {
  // Convert cents to dollars for display
  const subscriptionPriceDollars = subscriptionPriceCents ? Math.floor(subscriptionPriceCents / 100) : '';

  const handlePriceChange = (value: string, setter: (price: number | null) => void) => {
    if (!value) {
      setter(null);
      return;
    }

    // Remove any non-numeric characters
    const cleanValue = value.replace(/[^\d]/g, '');
    
    // Convert dollars to cents
    const dollars = parseInt(cleanValue);
    if (!isNaN(dollars) && dollars >= 0) {
      const cents = dollars * 100;
      setter(cents);
    }
  };

  const handleLimitChange = (value: string, setter: (limit: number | null) => void) => {
    if (!value) {
      setter(null);
      return;
    }
    const limit = parseInt(value);
    if (!isNaN(limit) && limit > 0) {
      setter(limit);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Payment Type
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => onPaymentTypeChange('free')}
            className={`flex items-center justify-center px-4 py-3 border-2 rounded-lg ${
              paymentType === 'free'
                ? 'border-purple-500 bg-purple-50 text-purple-700'
                : 'border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            <DollarSign className="h-4 w-4 mr-2" />
            <span className="font-medium">Free</span>
          </button>
          <button
            type="button"
            onClick={() => onPaymentTypeChange('subscription')}
            className={`flex items-center justify-center px-4 py-3 border-2 rounded-lg ${
              paymentType === 'subscription'
                ? 'border-purple-500 bg-purple-50 text-purple-700'
                : 'border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            <CreditCard className="h-4 w-4 mr-2" />
            <span className="font-medium">Subscription</span>
          </button>
        </div>
      </div>

      {paymentType === 'subscription' && (
        <div className="space-y-4">
          <div>
            <label htmlFor="subscriptionPrice" className="block text-sm font-medium text-gray-700">
              Monthly Subscription Price ($)
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                id="subscriptionPrice"
                min="1"
                value={subscriptionPriceDollars}
                onChange={(e) => handlePriceChange(e.target.value, onSubscriptionPriceChange)}
                className="block w-full pl-7 pr-12 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                placeholder="0"
              />
            </div>
          </div>

          <div>
            <label htmlFor="subscriptionLimit" className="block text-sm font-medium text-gray-700">
              Maximum Subscribers (optional)
            </label>
            <div className="mt-1">
              <input
                type="number"
                id="subscriptionLimit"
                min="1"
                value={subscriptionLimit || ''}
                onChange={(e) => handleLimitChange(e.target.value, onSubscriptionLimitChange)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                placeholder="No limit"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}